
import { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DownloadIcon, Play, Pause, FileIcon, FileImageIcon, FileTextIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";

export interface Attachment {
  id: string;
  type: "image" | "audio" | "document";
  url: string;
  name?: string;
  duration?: number; // Para áudios
  size?: number;
  mimeType?: string;
}

interface Message {
  id: number;
  sender: "user" | "ai";
  text: string;
  timestamp: string;
  attachments?: Attachment[];
}

interface ChatMessagesProps {
  messages: Message[];
}

export const ChatMessages = ({ messages }: ChatMessagesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  return (
    <ScrollArea className="h-full p-4">
      <div className="space-y-2">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <p className="text-muted-foreground text-sm">
              Nenhuma mensagem ainda. Comece uma conversa!
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.sender === "user" ? "justify-end" : "justify-start"
              } mb-4`}
            >
              {message.sender === "ai" && (
                <Avatar className="h-8 w-8 mr-2 flex-shrink-0">
                  <AvatarImage src="https://github.com/shadcn.png" alt="AI Avatar" />
                  <AvatarFallback>AI</AvatarFallback>
                </Avatar>
              )}
              
              <div
                className={cn(
                  "max-w-[70%] flex flex-col",
                  message.sender === "user" ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-lg p-3",
                    message.sender === "user"
                      ? "bg-[#D9FDD3] text-black rounded-tr-none"
                      : "bg-white text-black rounded-tl-none"
                  )}
                >
                  {message.text && (
                    <p className="text-sm whitespace-pre-wrap break-words mb-1">{message.text}</p>
                  )}
                  
                  {message.attachments?.map((attachment) => (
                    <MessageAttachment key={attachment.id} attachment={attachment} />
                  ))}
                </div>
                
                <div className={`mt-1 ${message.sender === "user" ? "text-right" : "text-left"}`}>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
              </div>
              
              {message.sender === "user" && (
                <Avatar className="h-8 w-8 ml-2 flex-shrink-0">
                  <AvatarImage src="https://github.com/shadcn.png" alt="User Avatar" />
                  <AvatarFallback>ME</AvatarFallback>
                </Avatar>
              )}
            </div>
          ))
        )}
        <div ref={scrollRef} />
      </div>
    </ScrollArea>
  );
};

// Função para formatar timestamp com tratamento de erros
const formatTimestamp = (timestamp: string): string => {
  try {
    // Verifica se o timestamp é uma string de hora no formato HH:mm
    if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(timestamp)) {
      return timestamp;
    }
    
    // Se for um ISO timestamp, tenta parsear
    if (timestamp.includes('T') || timestamp.includes('Z')) {
      const date = parseISO(timestamp);
      if (isValid(date)) {
        return format(date, "HH:mm", { locale: ptBR });
      }
    }
    
    // Tenta converter para data e verificar se é válida
    const date = new Date(timestamp);
    if (isValid(date)) {
      return format(date, "HH:mm", { locale: ptBR });
    }
    
    // Fallback para casos onde a data não é válida
    return timestamp || "Agora";
  } catch (error) {
    console.error("Erro ao formatar timestamp:", error, timestamp);
    return "Agora";
  }
};

const MessageAttachment = ({ attachment }: { attachment: Attachment }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const toggleAudioPlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  
  const handleAudioTimeUpdate = () => {
    if (!audioRef.current || !attachment.duration) return;
    const progress = (audioRef.current.currentTime / attachment.duration) * 100;
    setProgress(progress);
  };
  
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };
  
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };
  
  const formatDuration = (seconds?: number) => {
    if (!seconds) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };
  
  switch (attachment.type) {
    case "image":
      return (
        <div className="rounded-md overflow-hidden mb-2">
          <img src={attachment.url} alt={attachment.name || "Imagem"} className="max-w-full h-auto" />
          {attachment.name && (
            <div className="flex justify-between items-center text-xs px-2 py-1 bg-black/10">
              <span>{attachment.name}</span>
              <a href={attachment.url} download target="_blank" rel="noopener noreferrer">
                <DownloadIcon className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      );
      
    case "audio":
      return (
        <div className="flex items-center space-x-2 bg-black/5 rounded-md p-2 mb-2">
          <button onClick={toggleAudioPlayback} className="h-8 w-8 bg-primary rounded-full flex items-center justify-center text-white">
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-primary h-1.5 rounded-full" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-0.5">
              <span>{formatDuration((attachment.duration || 0) * (progress / 100))}</span>
              <span>{formatDuration(attachment.duration)}</span>
            </div>
          </div>
          
          <audio
            ref={audioRef}
            src={attachment.url}
            onTimeUpdate={handleAudioTimeUpdate}
            onEnded={handleAudioEnded}
            className="hidden"
          />
          
          <a href={attachment.url} download target="_blank" rel="noopener noreferrer" className="text-gray-500">
            <DownloadIcon className="h-4 w-4" />
          </a>
        </div>
      );
      
    case "document":
      const getFileIcon = () => {
        if (attachment.mimeType?.includes("image")) return <FileImageIcon className="h-6 w-6" />;
        if (attachment.mimeType?.includes("pdf")) return <FileTextIcon className="h-6 w-6" />;
        return <FileIcon className="h-6 w-6" />;
      };
      
      return (
        <div className="flex items-center space-x-3 bg-black/5 rounded-md p-2 mb-2">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
            {getFileIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{attachment.name || "Documento"}</p>
            <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
          </div>
          
          <a href={attachment.url} download target="_blank" rel="noopener noreferrer" className="text-gray-500">
            <DownloadIcon className="h-4 w-4" />
          </a>
        </div>
      );
      
    default:
      return null;
  }
};
